import {
	CUBE_SIZE_LARGE,
	CUBE_SIZE_SMALL,
	CUBE_SIZE_SMALL_CORNER,
	CUBE_SIZE_TALL,
	CUBE_SIZE_TALL_CORNER,
	CUBE_SIZE_WIDE
} from './global-constants';
import { ExportedBoxMath } from './box-math';

interface PricesEntry {
	readonly price: number;
	readonly backPrice?: number;
	readonly doorPrice?: number;
	readonly shelfPrice?: number;
	readonly drawerPrice?: number;
	readonly crossDivider?: number;
}

interface Prices {
	readonly [id: string]: PricesEntry;
}

export class ExportedBoxUtils {
	public isDebug: boolean = false;
	public readonly exportedBoxMath: ExportedBoxMath = new ExportedBoxMath();

	public prices: Prices = {
		'small': {
			price: 69,
			backPrice: 14,
			doorPrice: 24,
			shelfPrice: 14,
			drawerPrice: 39,
			crossDivider: 29
		},
		'tall': {
			price: 84,
			backPrice: 19,
			doorPrice: 29,
			shelfPrice: 14,
			drawerPrice: 39,
			crossDivider: 34
		},
		'wide': {
			price: 84,
			backPrice: 19,
			doorPrice: 29,
			shelfPrice: 19,
			drawerPrice: 59,
			crossDivider: 34
		},
		'large': {
			price: 99,
			backPrice: 24,
			doorPrice: 34,
			shelfPrice: 19,
			drawerPrice: 59,
			crossDivider: 39
		},
		'small-corner': {
			price: 69,
		},
		'tall-corner': {
			price: 84,
		},
	};

	public getBasePrices(): Prices {
		return this.prices;
	}

	public getCubeSize(cube): any {
		const size = cube.getAttr('cube_size');

		return size;
	}

	public isCorner(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_SMALL_CORNER || this.getCubeSize(cube) === CUBE_SIZE_TALL_CORNER;
	}

	public isCornerSize(size): boolean {
		return size === CUBE_SIZE_SMALL_CORNER || size === CUBE_SIZE_TALL_CORNER;
	}

	public isCubeSizeSmall(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_SMALL;
	}

	public isCubeSizeSmallCorner(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_SMALL_CORNER;
	}

	public isCubeSizeWide(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_WIDE;
	}

	public isCubeSizeTall(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_TALL;
	}

	public isCubeSizeTallCorner(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_TALL_CORNER;
	}

	public isCubeSizeLarge(cube): boolean {
		return this.getCubeSize(cube) === CUBE_SIZE_LARGE;
	}

	public setPosition(box, x, y): void {
		x = this.exportedBoxMath.getApproximation(x);
		y = this.exportedBoxMath.getApproximation(y);
		// x = Math.floor(x);
		// y = Math.floor(y);

		const initialX = box.attrs.x;
		const initialY = box.attrs.y;

		box.position({x, y});

		if (this.isDebug) {
			console.log(`Box position: (${initialX},${initialY})->(${x},${y})`);
		}
	};

	public getCubeColor(box): string {
		const color = box.attrs.cube_color;

		return color;
	}

	public encodeBack(box): string {
		const back = box.getAttr('back') ? this.getCubeColor(box) : '0';

		return back;
	}

	public hasBack(box): boolean {
		const has = box.getAttr('back');

		return has;
	}

	public encodeDoor(box): string {
		const door = box.getAttr('door') ? this.getCubeColor(box) : '0';

		return door;
	}

	public hasDoor(box): boolean {
		const has = box.getAttr('door');

		return has;
	}

	public getShelfCount(box): any {
		return box.getAttr('shelf');
	}

	public encodeShelf(box): string {
		const shelfCount = box.getAttr('shelf');
		const color = this.getCubeColor(box);

		let encoded;
		if (shelfCount == 0) {
			encoded = '0000';
		} else if (shelfCount == 1) {
			encoded = `${color}000`;
		} else if (shelfCount == 2) {
			encoded = `${color}${color}00`;
		} else if (shelfCount == 3) {
			encoded = `${color}${color}${color}0`;
		} else if (shelfCount == 4) {
			encoded = `${color}${color}${color}${color}`;
		}

		return encoded;
	}

	public hasShelf(box): boolean {
		const has = box.getAttr('shelf');

		return has;
	}

	public getDrawerCount(box): number {
		let counter = 0;

		if (this.hasTopDrawer(box)) {
			counter++;
		}

		if (this.hasMiddleDrawer(box)) {
			counter++;
		}

		if (this.hasBottomDrawer(box)) {
			counter++;
		}

		return counter;
	};

	public hasTopDrawer(box): boolean {
		return !!box.getAttr('top_drawer');
	}

	public hasMiddleDrawer(box): boolean {
		return !!box.getAttr('middle_drawer');
	}

	public hasBottomDrawer(box): boolean {
		return !!box.getAttr('bottom_drawer');
	}

	public hasCornerRight(box): boolean {
		return !!box.getAttr('corner_right');
	}

	public hasCornerLeft(box): boolean {
		return !box.getAttr('corner_right');
	}

	public hasCrossDivider(box): boolean {
		return !!box.getAttr('cross_divider');
	}

	public encodeDrawers(box): string {
		const color = this.getCubeColor(box);
		let encoded = '';

		switch (true) {
			case this.isCubeSizeSmall(box):
			case this.isCubeSizeWide(box):
				encoded =
						(this.hasBottomDrawer(box) ? `${color}` : '0') +
						(this.hasTopDrawer(box) ? `${color}0` : '00');
				break;
			case this.isCubeSizeLarge(box):
			case this.isCubeSizeTall(box):
				encoded =
						(this.hasBottomDrawer(box) ? `${color}` : '0') +
						(this.hasMiddleDrawer(box) ? `${color}` : '0') +
						(this.hasTopDrawer(box) ? `${color}` : '0');
				break;
		}

		return encoded;
	}

	public decodeDrawers(encoded, box): void {
		if (encoded[0] === 'n' || encoded[0] === 'w') {
			box.setAttr('bottom_drawer', true);
		} else {
			box.setAttr('bottom_drawer', false);
		}

		if (this.isCubeSizeSmall(box) || this.isCubeSizeWide(box)) {
			if (encoded[1] === 'n' || encoded[1] === 'w') {
				box.setAttr('top_drawer', true);
			} else {
				box.setAttr('top_drawer', false);
			}
		} else {
			if (encoded[1] === 'n' || encoded[1] === 'w') {
				box.setAttr('middle_drawer', true);
			} else {
				box.setAttr('middle_drawer', false);
			}

			if (encoded[2] === 'n' || encoded[2] === 'w') {
				box.setAttr('top_drawer', true);
			} else {
				box.setAttr('top_drawer', false);
			}
		}
	}

	public encodeCornerDirection(box): string {
		const cornerRightMaybe = box.getAttr('corner_right');

		if (!cornerRightMaybe) {
			return '';
		}

		const corner = cornerRightMaybe ? 'r' : 'l';

		return corner;
	}

	public changeIsDebug(debug): void {
		this.isDebug = debug;
	}
}
